import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  IconButton,
  Text,
  Collapse,
  Portal,
  useDisclosure,
  Flex,
  Badge,
  Avatar,
  Icon,
  Tooltip,
  useColorMode,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import {
  FiMenu,
  FiX,
  FiHome,
  FiUsers,
  FiUser,
  FiPackage,
  FiFolder,
  FiCalendar,
  FiDollarSign,
  FiCreditCard,
  FiSettings,
  FiLogOut,
  FiChevronRight,
  FiGrid,
  FiPhone,
  FiMail,
  FiLock,
  FiShield,
  FiUserPlus,
  FiCpu,
  FiBriefcase,
  FiDatabase,
  FiExternalLink,
  FiVideo,
  FiFilm,
  FiSun,
  FiMoon,
  FiMic,
  FiCompass,
  FiCheckSquare,
  FiRefreshCw,
  FiTruck,
  FiFileText,
  FiFlag,
  FiGift,
  FiTrendingUp,
  FiZap,
  FiTarget,
  FiShoppingCart,
  FiMessageSquare,
  FiBook,
  FiActivity,
  FiUploadCloud,
  FiDownload,
  FiKey,
  FiSend,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginWithSmsModal } from '../pages/authentication';
import { getColor, brandConfig } from '../brandConfig';
import { useShiftKey, isDevMode } from '../hooks/useShiftKey';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import {
  LAUNCHER, menuMaxH, LauncherEdgeTab, LauncherHideButton, useLauncherClickOutside,
} from './floating/LauncherChrome';
import { renderPermissionTooltip } from './devPermissionTooltip';

// Every size from LauncherChrome (draggable-floating-launchers.md § One file owns the numbers).
const BUTTON_SIZE = LAUNCHER.FAB_SIZE;
const EDGE_PAD = LAUNCHER.EDGE_PAD;

// Import module configs
import profileModuleConfig from '../pages/profile/moduleConfig';
// subscriptionModuleConfig removed — consolidated into billsModuleConfig
import clientsModuleConfig from '../pages/clients/moduleConfig';
import employeesModuleConfig from '../pages/employees/moduleConfig';
import companiesModuleConfig from '../pages/companies/moduleConfig';
import assetsModuleConfig from '../pages/assets/moduleConfig';
import { calendarsModuleConfig } from '../pages/calendars/moduleConfig';
import projectsModuleConfig from '../pages/projects/moduleConfig';
import billsModuleConfig from '../pages/bills/moduleConfig';
import emailsModuleConfig from '../pages/emails/moduleConfig';
import commsHealthModuleConfig from '../pages/comms-health/moduleConfig';
import smsModuleConfig from '../pages/sms/moduleConfig';
import commsLogModuleConfig from '../pages/comms-log/moduleConfig';
import passwordsModuleConfig from '../pages/passwords/moduleConfig';
import tenantManagementModuleConfig from '../pages/tenant-management/moduleConfig';
import moduleUpgradeModuleConfig from '../pages/module-upgrade/moduleConfig';
import aiOpsModuleConfig from '../pages/ai-ops/moduleConfig';
import ipfsManagementModuleConfig from '../pages/ipfs-management/moduleConfig';
import adminModuleConfig from '../pages/admin/moduleConfig';
import phoneSystemModuleConfig from '../pages/phone-system/moduleConfig';
// knowledgebaseModuleConfig removed - module deleted
import opportunitiesModuleConfig from '../pages/opportunities/moduleConfig';
import transcriptionsModuleConfig from '../pages/transcriptions/moduleConfig';
import proposalsModuleConfig from '../pages/proposals/moduleConfig';
import companyKnowledgeModuleConfig from '../pages/companyknowledge/moduleConfig';
import knowledgeExplorerModuleConfig from '../pages/knowledgeexplorer/moduleConfig';
import artifactsModuleConfig from '../pages/artifacts/moduleConfig';
import publicTrustModuleConfig from '../pages/public-trust/moduleConfig';
import hostingModuleConfig from '../pages/hosting/moduleConfig';
import lmModuleConfig from '../pages/lm/moduleConfig';
import frontendUpgradesModuleConfig from '../pages/frontend-upgrades/moduleConfig';
import seoMetadataModuleConfig from '../pages/seometadata/moduleConfig';
import siteEditorModuleConfig from '../pages/site-editor/moduleConfig';
import masterSiteEditorModuleConfig from '../pages/master-site-editor/moduleConfig';
import analyticsModuleConfig from '../pages/analytics/moduleConfig';
import mediaCDNModuleConfig from '../pages/media-cdn/moduleConfig';
import transcriptMinerModuleConfig from '../pages/transcript-miner/moduleConfig';
import vlogModuleConfig from '../pages/vlog/moduleConfig';
import stateOfPlayModuleConfig from '../pages/state-of-play/moduleConfig';
import sovereigntyModuleConfig from '../pages/sovereignty/moduleConfig';
import valueLiteracyModuleConfig from '../pages/value-literacy/moduleConfig';
import governanceModuleConfig from '../pages/governance/moduleConfig';
import agentBoardModuleConfig from '../pages/agent-board/moduleConfig';
import bankingModuleConfig from '../pages/banking/moduleConfig';
import walletModuleConfig from '../pages/wallet/moduleConfig';
import pModuleConfig from '../pages/p/moduleConfig';
import rewardsModuleConfig from '../pages/rewards/moduleConfig';
import growthEngineModuleConfig from '../pages/growth-engine/moduleConfig';
import launchModuleConfig from '../pages/launch/moduleConfig';
import leadflowModuleConfig from '../pages/leadflow/moduleConfig';
import energyTrackerModuleConfig from '../pages/energy-tracker/moduleConfig';
import propertyIntelModuleConfig from '../pages/property-intel/moduleConfig';
import p2ModuleConfig from '../pages/p2/moduleConfig';
import dataexportModuleConfig from '../pages/dataexport/moduleConfig';
// Renamed tom-manager → client-journey (v2.0). Folder kept; id is now "client-journey".
import clientJourneyModuleConfig from '../pages/tom-manager/moduleConfig';
import vaultInsightsModuleConfig from '../pages/vault-insights/moduleConfig';
import meetModuleConfig from '../pages/meet/moduleConfig';
import pokerCoachModuleConfig from "../pages/poker-coach/moduleConfig";
import club52ModuleConfig from '../pages/club52/moduleConfig';
import { useNavbarState } from '../hooks/useNavbarState';

// Animations
const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
  100% { transform: translateY(0px); }
`;

// Icon mapping for modules
export const moduleIcons: Record<string, any> = {
  profile: FiUser,
  meet: FiVideo,
  'poker-coach': FiTarget,
  club52: FiTarget,
  clients: FiUsers,
  employees: FiBriefcase,
  companies: FiDatabase,
  assets: FiPackage,
  calendars: FiCalendar,
  sessions: FiCalendar,
  projects: FiFolder,
  bills: FiDollarSign,
  opportunities: FiDollarSign,
  proposals: FiExternalLink,
  emails: FiMail,
  'comms-health': FiActivity,
  'ai-ops': FiTrendingUp,
  passwords: FiLock,
  'phone-system': FiPhone,
  transcriptions: FiMic,
  // knowledgebase: FiDatabase, // removed - module deleted
  companyknowledge: FiDatabase,
  knowledgeexplorer: FiBook,
  artifacts: FiFileText,
  'public-trust': FiFlag,
  hosting: FiUploadCloud,
  'vault-insights': FiZap,
  'sms': FiMessageSquare,
  'comms-log': FiSend,
  'lm': FiDatabase,
  subscriptions: FiDollarSign,
  'frontend-upgrades': FiRefreshCw,
  'tenant-management': FiDatabase,
  admin: FiSettings,
  seometadata: FiExternalLink,
  'site-editor': FiCpu,
  'master-site-editor': FiSettings,
  'media-cdn': FiDatabase,
  'transcript-miner': FiVideo,
  'vlog': FiFilm,
  'state-of-play': FiMic,
  'sovereignty': FiCompass,
  'value-literacy': FiCompass,
  'governance': FiCheckSquare,
  'agent-board': FiCpu,
  'banking': FiCreditCard,
  'wallet': FiKey,
  'p': FiFileText,
  'rewards': FiGift,
  'growth-engine': FiTrendingUp,
  'tom': FiZap,
  'client-journey': FiCpu,
  'leadflow': FiTarget,
  'p2': FiShoppingCart,
  'property-intel': FiHome,
  'dataexport': FiDownload,
};

// All available module configs — module-level so other surfaces (e.g. the
// admin home-page quick-links grid) can reuse the single canonical list
// instead of maintaining a divergent copy.
export const allModuleConfigs = [
  profileModuleConfig,
  clientsModuleConfig,
  employeesModuleConfig,
  companiesModuleConfig,
  assetsModuleConfig,
  calendarsModuleConfig,
  passwordsModuleConfig,
  projectsModuleConfig,
  billsModuleConfig,
  opportunitiesModuleConfig,
  proposalsModuleConfig,
  emailsModuleConfig,
  commsHealthModuleConfig,
  smsModuleConfig,
  commsLogModuleConfig,
  phoneSystemModuleConfig,
  meetModuleConfig,
  pokerCoachModuleConfig,
  club52ModuleConfig,
  transcriptionsModuleConfig,
  // subscriptionModuleConfig removed — consolidated into billsModuleConfig
  // knowledgebaseModuleConfig, // removed - module deleted
  companyKnowledgeModuleConfig,
  knowledgeExplorerModuleConfig,
  artifactsModuleConfig,
  publicTrustModuleConfig,
  hostingModuleConfig,
  lmModuleConfig,
  frontendUpgradesModuleConfig,
  seoMetadataModuleConfig,
  siteEditorModuleConfig,
  masterSiteEditorModuleConfig,
  analyticsModuleConfig,
  mediaCDNModuleConfig,
  transcriptMinerModuleConfig,
  vlogModuleConfig,
  stateOfPlayModuleConfig,
  sovereigntyModuleConfig,
  valueLiteracyModuleConfig,
  governanceModuleConfig,
  agentBoardModuleConfig,
  bankingModuleConfig,
  walletModuleConfig,
  pModuleConfig,
  rewardsModuleConfig,
  growthEngineModuleConfig,
  launchModuleConfig,
  leadflowModuleConfig,
  energyTrackerModuleConfig,
  propertyIntelModuleConfig,
  p2ModuleConfig,
  dataexportModuleConfig,
  clientJourneyModuleConfig,
  vaultInsightsModuleConfig,
  tenantManagementModuleConfig,
  moduleUpgradeModuleConfig,
  aiOpsModuleConfig,
  ipfsManagementModuleConfig,
  adminModuleConfig,
];

interface FloatingNavbarProps {
  modules?: any[]; // Resolved modules from App.tsx with backend permissions
}

export const FloatingNavbar: React.FC<FloatingNavbarProps> = ({ modules: resolvedModules }) => {
  // Use persistent state management with client-specific storage
  const { isAuthenticated, user, logout, refreshAuth } = useAuth();
  const { colorMode, toggleColorMode } = useColorMode();
  // Use client ID for user-specific navbar state
  const clientId = user?.clientId || user?.client?.id || user?.id;


  const {
    isExpanded,
    setIsExpanded,
    activeCategory,
    setActiveCategory,
    expandedModules,
    toggleModuleExpanded,
    resetState,
  } = useNavbarState(clientId);


  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const { isOpen: isLoginOpen, onOpen: onLoginOpen, onClose: onLoginClose } = useDisclosure();
  const navigate = useNavigate();

  // Dev-mode Shift+hover permission reveal — see vault/how-to/dev-mode-permission-hover.md
  const shiftHeld = useShiftKey();
  const devReveal = isDevMode() && shiftHeld;

  // Per-user draggable launcher position (persists across sessions).
  // See vault/how-to/draggable-floating-launchers.md for the full pattern.
  const { position, setPosition, dragHandlers, wasJustDragged } = useDraggableFloat({
    storageKey: `tms_floating_navbar_pos_${clientId || 'guest'}`,
    size: BUTTON_SIZE,
    edgePad: EDGE_PAD,
    // Bottom-left, clearing LAUNCHER.FAB_BOTTOM_CLEARANCE so the FAB never lands on a
    // composer or sticky action bar (see the constant). Only affects users who have never
    // dragged it — a saved position is theirs to keep, and the footer's "↻ Reset floating
    // panels" re-homes anyone who wants the new default.
    defaultPosition: (_vw, vh) => ({ left: 20, top: vh - BUTTON_SIZE - LAUNCHER.FAB_BOTTOM_CLEARANCE }),
  });

  // Hide/show the whole launcher (2026-08-10) — requested for mobile, where the
  // FAB costs real calendar space. Hidden is per-user persisted, and a tiny
  // translucent edge tab ALWAYS remains, so a user can never strand themselves
  // without navigation. The calendars toolbar's "Reset panels" un-hides and
  // re-homes it via a window CustomEvent (a `storage` listener wouldn't do —
  // that event only fires in OTHER tabs). The event name + key shape are a
  // string contract with src/pages/calendars/utils/floatingPanels.ts — the
  // module can't be imported from app shell (import contract), so keep them
  // in sync BY HAND if either ever changes.
  const navHiddenKey = `tms_floating_navbar_hidden_${clientId || 'guest'}`;
  const [navHidden, setNavHidden] = useState<boolean>(() => {
    try {
      return localStorage.getItem(navHiddenKey) === '1';
    } catch {
      return false;
    }
  });
  const hideNav = (hidden: boolean) => {
    setNavHidden(hidden);
    if (hidden) setIsExpanded(false);
    try {
      localStorage.setItem(navHiddenKey, hidden ? '1' : '0');
    } catch { /* noop */ }
  };
  useEffect(() => {
    const onReset = () => {
      setNavHidden(false);
      // Self-clearing (2026-08-15 standard): remove our OWN keys so a
      // dispatch-only reset survives reload (setPosition never persists).
      try {
        localStorage.removeItem(navHiddenKey);
        localStorage.removeItem(`tms_floating_navbar_pos_${clientId || 'guest'}`);
      } catch { /* noop */ }
      setPosition({ left: 20, top: window.innerHeight - 76 });
    };
    window.addEventListener('tms:reset-floating-panels', onReset);
    return () => window.removeEventListener('tms:reset-floating-panels', onReset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navHiddenKey]);

  // Viewport dimensions tracked in state so computeMenuPlacement recomputes on resize.
  const [viewport, setViewport] = useState(() =>
    typeof window !== 'undefined'
      ? { w: window.innerWidth, h: window.innerHeight }
      : { w: 1200, h: 800 }
  );
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const panelPlacement = computeMenuPlacement(
    { left: position.left, top: position.top, size: BUTTON_SIZE },
    viewport,
    { preferredWidth: Math.max(360, LAUNCHER.MENU_W), estimatedHeight: 520, gap: LAUNCHER.MENU_GAP },
    EDGE_PAD,
  );

  // Refresh auth data when component mounts and user is authenticated but data is missing
  useEffect(() => {

    if (isAuthenticated && (!user || !user.permissions)) {
      refreshAuth();
    }
  }, [isAuthenticated, user, refreshAuth]);

  // Merge resolved permissions from backend (via App.tsx) into internal module list.
  // Modules migrated to single-source-of-truth have empty permission arrays in their config
  // and get real permissions resolved from the backend at runtime.
  const mergedModuleConfigs = React.useMemo(() => {
    if (!resolvedModules) return allModuleConfigs;
    const resolvedMap = new Map(resolvedModules.map((m: any) => [m.id, m]));
    return allModuleConfigs.map(mod => {
      const resolved = resolvedMap.get(mod.id);
      if (!resolved) return mod; // Not in App.tsx's list — keep as-is
      return { ...mod, permissions: resolved.permissions, navigation: resolved.navigation };
    });
  }, [resolvedModules]);

  // Filter modules based on user permissions
  const getAvailableModules = () => {
    // console.log('📦 [4] FloatingNavbar - Getting available modules', {
    //   totalModules: allModuleConfigs.length,
    //   hasUser: !!user,
    //   userPermissions: user?.permissions || [],
    // });

    if (!user || !user.permissions || user.permissions.length === 0) {
      return [];
    }

    const filteredModules = mergedModuleConfigs.filter(module => {
      const hasPermission = user?.permissions?.some((permission: string) => {
        // Check for view permissions in the standard format
        const hasViewPermission = module.permissions?.view?.includes(permission) || false;
        if (hasViewPermission) {
          // console.log('✅ [6] FloatingNavbar - Module accessible', {
          //   module: module.id,
          //   permission,
          //   viewPermissions: module.permissions.view,
          // });
        }
        return hasViewPermission;
      }) || false;

      return hasPermission;
    });


    return filteredModules;
  };

  // Group modules by category
  const categorizedModules = {
    // 'Core': ['profile', 'clients', 'employees', 'companies'],
    'Core': ['leadflow', 'bills', 'banking', 'opportunities', 'proposals', 'p', 'clients', 'rewards', 'growth-engine', 'launch', 'p2', 'hosting', 'agent-board'],

    'Human Management': ['employees', 'companies', 'passwords'],

    'Delivery': [ 'projects', 'assets', 'lm', 'sovereignty', 'value-literacy'],

    'Communication': ['calendars', 'emails', 'comms-health', 'comms-log', 'phone-system', 'sms', 'meet', 'state-of-play', 'governance', 'wallet', 'club52', 'poker-coach',],

    'Media & Content': ['companyknowledge', 'knowledgeexplorer', 'artifacts', 'transcriptions', 'media-cdn', 'transcript-miner', 'vlog'],

    'Research': ['energy-tracker', 'property-intel', 'public-trust'],

    'Sell Ready': ['assets',],




    'To Improve': ['sessions', 'subscriptions'],


    'Settings': ['profile', 'tenant-management', 'module-upgrade', 'ai-ops', 'local-private-ipfs-management', 'admin', 'client-journey', 'site-editor', 'frontend-upgrades', 'seometadata', 'analytics', 'master-site-editor', 'vault-insights', 'dataexport'],


  };

  const availableModules = isAuthenticated ? getAvailableModules() : [];


  const handleNavItemClick = (path: string) => {
    navigate(path);
    // Keep the menu state but close the expanded view
    setIsExpanded(false);
  };

  const handleOpenInNewTab = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsExpanded(false);
    // Reset navbar state on logout
    resetState();
  };

  // Filter navigation items based on user permissions
  const getVisibleNavItems = (navigation: any) => {
    if (!navigation) return [];

    // Handle different navigation formats
    let navItems: any[] = [];
    if (Array.isArray(navigation)) {
      navItems = navigation;
    } else if (navigation.sub && Array.isArray(navigation.sub)) {
      navItems = navigation.sub;
    }

    return navItems.filter((item: any) => {
      // If no permissions required, show to everyone
      if (!item.permissions || item.permissions.length === 0) return true;

      // Check if user has required permission
      // Note: ADMIN bypass is handled by the backend — every module's permissions.ts
      // includes ClientPermission.ADMIN in its permission arrays, so ADMIN users
      // always match. No frontend hack needed.
      const hasPermission = user?.permissions?.some((permission: string) =>
        item.permissions.includes(permission)
      ) || false;

      return hasPermission;
    });
  };

  // Close menu when clicking outside. With three Portals (menu + main FAB + mini-logout FAB)
  // we must exempt all three containers so clicks inside any of them don't close the menu.
  // Close menu when tapping outside the FAB, the menu panel, or the emergency-logout
  // mini-FAB — three Portals, three classNames to exempt. pointerdown-based
  // (LauncherChrome) so a finger tap outside closes it too.
  const closeMenu = useCallback(() => {
    setIsExpanded(false);
    setActiveCategory(null);
  }, [setIsExpanded, setActiveCategory]);
  useLauncherClickOutside(
    isExpanded,
    ['.floating-navbar-container', '.floating-navbar-menu', '.floating-navbar-logout'],
    closeMenu,
  );

  return (
    <>
      {/* ===== Hidden state — ONLY a translucent edge tab renders. Tapping it
          brings the whole launcher back. Never remove this: it is the guarantee
          that hiding the nav can't strand anyone. ===== */}
      {navHidden && (
        <Portal>
          <LauncherEdgeTab
            edge="left"
            top={position.top}
            bg={getColor('secondary', colorMode)}
            zIndex={LAUNCHER.Z_FAB}
            label="Show the navigation button again"
            onClick={() => hideNav(false)}
          />
        </Portal>
      )}

      {/* ===== Portal A — Menu panel (viewport-safe placement via computeMenuPlacement) ===== */}
      {!navHidden && (
      <Portal>
        <Box
          className="floating-navbar-menu"
          position="fixed"
          {...(panelPlacement.top !== undefined ? { top: `${panelPlacement.top}px` } : {})}
          {...(panelPlacement.bottom !== undefined ? { bottom: `${panelPlacement.bottom}px` } : {})}
          left={`${panelPlacement.left}px`}
          width={`${panelPlacement.width}px`}
          zIndex={LAUNCHER.Z_MENU}
          pointerEvents={isExpanded ? 'auto' : 'none'}
        >
          <Collapse in={isExpanded} animateOpacity>
            <Box
              bg={colorMode === 'light'
                ? "rgba(255, 255, 255, 0.98)"
                : "rgba(10, 10, 15, 0.95)"}
              backdropFilter="blur(20px)"
              borderRadius="2xl"
              border="1px solid"
              borderColor={getColor("border.light", colorMode)}
              p={4}
              // Clamped by the hook to the room on the chosen side of the
              // FAB, so the open menu is ALWAYS fully on screen and can
              // never resize/scroll the page (2026-08-20 standard).
              maxH={menuMaxH(panelPlacement)}
              overflowY="auto"
              overscrollBehavior="contain"
              boxShadow={colorMode === 'light'
                ? "0 10px 40px rgba(0, 0, 0, 0.15)"
                : "0 20px 60px rgba(0, 0, 0, 0.8)"}
              animation={`${slideIn} 0.3s ease-out`}
              css={{
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: getColor("secondary", colorMode),
                  borderRadius: '2px',
                },
              }}
            >
              {/* Theme Toggle — hidden when the brand is locked to one palette (brandConfig.colorModeLocked) */}
              {!brandConfig.colorModeLocked && (
                <Flex justify="flex-end" mb={3}>
                  <IconButton
                    aria-label="Toggle color mode"
                    icon={colorMode === 'light' ? <FiMoon /> : <FiSun />}
                    size="md"
                    variant="ghost"
                    color={colorMode === 'light' ? 'gray.600' : 'gray.300'}
                    onClick={toggleColorMode}
                    _hover={{
                      bg: colorMode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </Flex>
              )}

              {/* User Profile Section — ALWAYS rendered when authenticated, even if
                  `user` is null (profile failed to load). Logout stays reachable so
                  broken auth state can never lock the user in. */}
              {isAuthenticated && (
                <Flex
                  align="center"
                  justify="space-between"
                  mb={4}
                  p={3}
                  bg={colorMode === 'light' ? "rgba(90, 200, 250, 0.05)" : "rgba(90, 200, 250, 0.1)"}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'light' ? "rgba(90, 200, 250, 0.1)" : "rgba(90, 200, 250, 0.2)"}
                >
                  <HStack
                    spacing={3}
                    cursor={user ? 'pointer' : 'default'}
                    onClick={user ? () => handleNavItemClick('/profile') : undefined}
                    _hover={user ? { opacity: 0.8 } : undefined}
                    transition="opacity 0.2s"
                  >
                    {user ? (
                      <>
                        <Avatar
                          size="sm"
                          name={`${user.fName || user.firstName || ""} ${user.lName || user.lastName || ""}`}
                          src={user.profilePhoto || undefined}
                          bg={getColor("secondary", colorMode)}
                        />
                        <VStack align="start" spacing={0}>
                          <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? "gray.700" : "white"}>
                            {user.fName || user.firstName || 'User'} {user.lName || user.lastName || ''}
                          </Text>
                          <Text fontSize="xs" color={colorMode === 'light' ? "gray.500" : "gray.400"}>
                            {user.email}
                          </Text>
                        </VStack>
                      </>
                    ) : (
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="600" color={colorMode === 'light' ? "gray.700" : "white"}>
                          Signed in
                        </Text>
                        <Text fontSize="xs" color="orange.400">
                          Profile not loaded — try logout
                        </Text>
                      </VStack>
                    )}
                  </HStack>
                  <IconButton
                    aria-label="Logout"
                    icon={<FiLogOut />}
                    size="md"
                    variant="ghost"
                    color="red.400"
                    onClick={handleLogout}
                    _hover={{
                      bg: 'rgba(255, 0, 0, 0.1)',
                      transform: 'scale(1.1)',
                    }}
                  />
                </Flex>
              )}

              {/* Account links — Security for everyone (nudges passkey setup),
                  Onboarding for users with the ONBOARDING permission. */}
              {isAuthenticated && user && (
                <VStack align="stretch" spacing={1} mb={4}>
                  <Button size="md" minH={`${LAUNCHER.ROW_MIN_H}px`} variant="ghost" justifyContent="flex-start" leftIcon={<FiShield />} onClick={() => handleNavItemClick('/security')}>
                    Security &amp; sign-in
                  </Button>
                  {(user.permissions?.includes('ONBOARDING') || user.permissions?.includes('MASTER_ADMIN')) && (
                    <Button size="md" minH={`${LAUNCHER.ROW_MIN_H}px`} variant="ghost" justifyContent="flex-start" leftIcon={<FiUserPlus />} onClick={() => handleNavItemClick('/onboarding')}>
                      Onboard people
                    </Button>
                  )}
                </VStack>
              )}

              {/* Quick Actions */}
              <HStack mb={4} spacing={2}>
                <Button
                  size="md"
                  minH={`${LAUNCHER.ROW_MIN_H}px`}
                  leftIcon={<FiHome />}
                  bg={getColor("secondary", colorMode)}
                  color="white"
                  fontWeight="600"
                  borderRadius="full"
                  _hover={{
                    bg: getColor("secondaryHover", colorMode),
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.2s"
                  onClick={() => {
                    navigate('/');
                    setIsExpanded(false);
                  }}
                >
                  Home
                </Button>
                {!isAuthenticated && (
                  <Button
                    size="md"
                    minH={`${LAUNCHER.ROW_MIN_H}px`}
                    bg={getColor("secondary", colorMode)}
                    color="white"
                    fontWeight="600"
                    borderRadius="full"
                    onClick={onLoginOpen}
                    _hover={{
                      bg: getColor("secondaryHover", colorMode),
                      transform: 'translateY(-2px)',
                    }}
                    transition="all 0.2s"
                  >
                    Sign In
                  </Button>
                )}
              </HStack>

              {/* Module Categories */}
              {isAuthenticated && (
                <VStack spacing={2} align="stretch">
                  {Object.entries(categorizedModules).map(([category, moduleIds]) => {
                    const categoryModules = availableModules.filter(m => 
                      moduleIds.includes(m.id)
                    );
                    
                    if (categoryModules.length === 0) return null;

                    return (
                      <Box key={category}>
                        <Button
                          size="md"
                          minH={`${LAUNCHER.ROW_MIN_H}px`}
                          variant="ghost"
                          color={colorMode === 'light' ? "gray.600" : "gray.400"}
                          fontSize="sm"
                          fontWeight="600"
                          textTransform="uppercase"
                          letterSpacing="wider"
                          justifyContent="space-between"
                          w="full"
                          onClick={() => {
                            const newCategory = activeCategory === category ? null : category;
                            setActiveCategory(newCategory);
                          }}
                          _hover={{
                            bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                            color: getColor("secondary", colorMode),
                          }}
                          rightIcon={
                            <Box
                              transform={activeCategory === category ? 'rotate(90deg)' : 'rotate(0)'}
                              transition="transform 0.2s"
                            >
                              <FiChevronRight />
                            </Box>
                          }
                        >
                          {category}
                        </Button>
                        
                        <Collapse in={activeCategory === category} animateOpacity>
                          <VStack spacing={1} align="stretch" mt={2} pl={4}>
                            {categoryModules.map((module) => {
                              const IconComponent = moduleIcons[module.id] || FiGrid;
                              const isModuleExpanded = expandedModules.has(module.id);
                              const visibleNavItems = getVisibleNavItems(module.navigation);
                              
                              // Get the main module path (handle different navigation formats)
                              let mainModulePath = `/module/${module.id}`;
                              if (module.navigation) {
                                if (Array.isArray(module.navigation) && module.navigation[0]) {
                                  mainModulePath = module.navigation[0].path;
                                } else if (!Array.isArray(module.navigation) && typeof module.navigation === 'object' && 'main' in module.navigation) {
                                  mainModulePath = (module.navigation as any).main.path;
                                }
                              } else if (module.routes?.[0]) {
                                mainModulePath = module.routes[0].path;
                              }
                              
                              // Permissions for the module-level button — use the first
                              // nav item's permissions since that's where the click lands.
                              const moduleButtonPerms: string[] =
                                (visibleNavItems[0]?.permissions as string[] | undefined) ?? [];
                              const moduleButtonLevel = visibleNavItems[0]?.permissionLevel as string | undefined;

                              return (
                                <Box key={module.id}>
                                  <HStack
                                    spacing={0}
                                    onMouseEnter={() => setHoveredItem(`module-${module.id}`)}
                                    onMouseLeave={() => setHoveredItem(null)}
                                  >
                                    <Tooltip
                                      label={
                                        devReveal
                                          ? renderPermissionTooltip({
                                              label: module.name,
                                              path: mainModulePath,
                                              moduleId: module.id,
                                              permissionLevel: moduleButtonLevel,
                                              resolved: moduleButtonPerms,
                                            })
                                          : module.name
                                      }
                                      placement="right"
                                      hasArrow
                                      isDisabled={!devReveal}
                                      bg={devReveal ? 'gray.800' : undefined}
                                      color={devReveal ? 'white' : undefined}
                                      px={devReveal ? 3 : undefined}
                                      py={devReveal ? 2 : undefined}
                                    >
                                    <Button
                                      size="md"
                                      minH={`${LAUNCHER.ROW_MIN_H}px`}
                                      variant="ghost"
                                      color={colorMode === 'light' ? "gray.700" : "gray.300"}
                                      flex="1"
                                      justifyContent="flex-start"
                                      leftIcon={<Icon as={IconComponent} />}
                                      onClick={() => {
                                        // Navigate to the main module page
                                        handleNavItemClick(mainModulePath);
                                      }}
                                      position="relative"
                                      _hover={{
                                        bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                        color: getColor("secondary", colorMode),
                                        transform: 'translateX(4px)',
                                        _before: {
                                          opacity: 1,
                                        }
                                      }}
                                      _before={{
                                        content: '""',
                                        position: 'absolute',
                                        left: '-16px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: '3px',
                                        height: '70%',
                                        bg: `linear-gradient(180deg, transparent, ${getColor("secondary", colorMode)}, transparent)`,
                                        borderRadius: 'full',
                                        opacity: 0,
                                        transition: 'opacity 0.2s',
                                      }}
                                      transition="all 0.2s"
                                    >
                                      {module.name}
                                    </Button>
                                    </Tooltip>

                                    {/* Open in new tab button - shows on hover */}
                                    <IconButton
                                      aria-label="Open in new tab"
                                      icon={<FiExternalLink size="14" />}
                                      size="sm"
                                      minW={`${LAUNCHER.ROW_MIN_H}px`}
                                      h={`${LAUNCHER.ROW_MIN_H}px`}
                                      variant="ghost"
                                      color="gray.400"
                                      opacity={hoveredItem === `module-${module.id}` ? 1 : 0}
                                      pointerEvents={hoveredItem === `module-${module.id}` ? 'auto' : 'none'}
                                      onClick={() => handleOpenInNewTab(mainModulePath)}
                                      _hover={{
                                        bg: 'rgba(90, 200, 250, 0.2)',
                                        color: getColor("secondary", colorMode),
                                      }}
                                      transition="all 0.2s"
                                    />
                                    
                                    {/* Expand/collapse button */}
                                    {visibleNavItems.length > 0 && (
                                      <IconButton
                                        aria-label="Expand module"
                                        icon={
                                          <Box
                                            transform={isModuleExpanded ? 'rotate(90deg)' : 'rotate(0)'}
                                            transition="transform 0.2s"
                                          >
                                            <FiChevronRight size="16" />
                                          </Box>
                                        }
                                        size="sm"
                                        minW={`${LAUNCHER.ROW_MIN_H}px`}
                                        h={`${LAUNCHER.ROW_MIN_H}px`}
                                        variant="ghost"
                                        color="gray.400"
                                        onClick={() => {
                                          toggleModuleExpanded(module.id);
                                        }}
                                        _hover={{
                                          bg: colorMode === 'light' ? 'rgba(90, 200, 250, 0.1)' : 'rgba(90, 200, 250, 0.15)',
                                          color: getColor("secondary", colorMode),
                                        }}
                                      />
                                    )}
                                  </HStack>
                                  
                                  {/* Sub-navigation items */}
                                  <Collapse in={isModuleExpanded} animateOpacity>
                                    <VStack spacing={0.5} align="stretch" mt={1} pl={8}>
                                      {visibleNavItems.map((navItem: any) => (
                                        <HStack
                                          key={navItem.path}
                                          spacing={0}
                                          onMouseEnter={() => setHoveredItem(`nav-${navItem.path}`)}
                                          onMouseLeave={() => setHoveredItem(null)}
                                        >
                                          <Tooltip
                                            label={
                                              devReveal
                                                ? renderPermissionTooltip({
                                                    label: navItem.label,
                                                    path: navItem.path,
                                                    moduleId: module.id,
                                                    permissionLevel: navItem.permissionLevel,
                                                    resolved: (navItem.permissions as string[]) ?? [],
                                                  })
                                                : navItem.label
                                            }
                                            placement="right"
                                            hasArrow
                                            isDisabled={!devReveal}
                                            bg={devReveal ? 'gray.800' : undefined}
                                            color={devReveal ? 'white' : undefined}
                                            px={devReveal ? 3 : undefined}
                                            py={devReveal ? 2 : undefined}
                                          >
                                          <Button
                                            size="sm"
                                            minH={`${LAUNCHER.ROW_MIN_H}px`}
                                            variant="ghost"
                                            color={colorMode === 'light' ? "gray.600" : "gray.400"}
                                            flex="1"
                                            justifyContent="flex-start"
                                            leftIcon={navItem.icon ? <Text fontSize="sm">{navItem.icon}</Text> : undefined}
                                            onClick={() => handleNavItemClick(navItem.path)}
                                            fontSize="sm"
                                            fontWeight="400"
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.1)',
                                              color: getColor("secondary", colorMode),
                                              transform: 'translateX(2px)',
                                            }}
                                            transition="all 0.15s"
                                          >
                                            {navItem.label}
                                            {navItem.badge && (
                                              <Badge
                                                ml={1}
                                                colorScheme={navItem.badge.color || 'gray'}
                                                fontSize="2xs"
                                                variant="subtle"
                                              >
                                                {navItem.badge.text}
                                              </Badge>
                                            )}
                                          </Button>
                                          </Tooltip>
                                          
                                          {/* Open in new tab button for sub-items */}
                                          <IconButton
                                            aria-label="Open in new tab"
                                            icon={<FiExternalLink size="12" />}
                                            size="sm"
                                            variant="ghost"
                                            color="gray.500"
                                            opacity={hoveredItem === `nav-${navItem.path}` ? 1 : 0}
                                            pointerEvents={hoveredItem === `nav-${navItem.path}` ? 'auto' : 'none'}
                                            onClick={() => handleOpenInNewTab(navItem.path)}
                                            minW={`${LAUNCHER.ROW_MIN_H}px`}
                                            h={`${LAUNCHER.ROW_MIN_H}px`}
                                            _hover={{
                                              bg: 'rgba(90, 200, 250, 0.2)',
                                              color: getColor("secondary", colorMode),
                                            }}
                                            transition="all 0.2s"
                                          />
                                        </HStack>
                                      ))}
                                    </VStack>
                                  </Collapse>
                                </Box>
                              );
                            })}
                          </VStack>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </Collapse>
        </Box>
      </Portal>
      )}

      {/* ===== Portal B — Main FAB (draggable, pinned at user-persisted position) ===== */}
      {!navHidden && (
      <Portal>
        <Box
          className="floating-navbar-container"
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          zIndex={LAUNCHER.Z_FAB}
        >
          <IconButton
            aria-label="Toggle navigation — drag to move, tap to open"
            icon={isExpanded ? <FiX size={24} /> : <FiMenu size={24} />}
            size="lg"
            w={`${BUTTON_SIZE}px`}
            h={`${BUTTON_SIZE}px`}
            minW={`${BUTTON_SIZE}px`}
            borderRadius="full"
            bg={getColor("secondary", colorMode)}
            color="white"
            border="2px solid"
            borderColor="rgba(255, 255, 255, 0.2)"
            boxShadow="0 4px 12px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
            {...dragHandlers}
            onClick={() => {
              if (wasJustDragged()) return;
              setIsExpanded(!isExpanded);
            }}
            _hover={{
              bg: getColor("secondaryHover", colorMode),
              transform: 'scale(1.1) rotate(180deg)',
              boxShadow: "0 6px 20px rgba(90, 200, 250, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
            }}
            _active={{
              transform: 'scale(0.95)',
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3), inset 0 2px 4px rgba(0, 0, 0, 0.2)"
            }}
            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            animation={!isExpanded ? `${float} 3s ease-in-out infinite` : undefined}
          />
          {/* Hide mini-FAB — tucks the launcher away to the edge tab. Sits by
              the emergency-logout mini so the affordances travel together. */}
          <LauncherHideButton
            fabSize={BUTTON_SIZE}
            wasJustDragged={wasJustDragged}
            label="Hide the nav button — a small tab stays on the left edge to bring it back. The footer's ↻ Reset floating panels button also restores it."
            onClick={() => hideNav(true)}
          />
        </Box>
      </Portal>
      )}

      {/* ===== Portal C — Emergency logout mini-FAB (always visible when authenticated) =====
          Escape hatch for broken auth state: if permissions never load, `user` is null,
          or the menu won't open for any reason, tapping this immediately clears the
          session. Sits directly above the main FAB and tracks its dragged position. */}
      {isAuthenticated && !navHidden && (
        <Portal>
          <Box
            className="floating-navbar-logout"
            position="fixed"
            top={`${Math.max(EDGE_PAD, position.top - 40)}px`}
            left={`${position.left + 14}px`}
            zIndex={LAUNCHER.Z_MINI}
          >
            <IconButton
              aria-label="Emergency logout (always visible)"
              icon={<FiLogOut />}
              size="sm"
              borderRadius="full"
              bg="red.400"
              color="white"
              border="2px solid"
              borderColor="rgba(255, 255, 255, 0.3)"
              boxShadow="0 2px 8px rgba(255, 0, 0, 0.3)"
              opacity={0.85}
              onClick={handleLogout}
              _hover={{
                opacity: 1,
                bg: 'red.500',
                transform: 'scale(1.15)',
                boxShadow: '0 4px 12px rgba(255, 0, 0, 0.5)',
              }}
              transition="all 0.2s"
            />
          </Box>
        </Portal>
      )}

      {/* Login Modal */}
      <LoginWithSmsModal
        isOpen={isLoginOpen}
        onClose={onLoginClose}
        onLoginSuccess={() => {
          onLoginClose();
          window.location.reload();
        }}
      />
    </>
  );
};
